/**
 * Knowledge Articles Loading System
 *
 * This module handles loading and parsing knowledge base articles.
 * It follows the same pattern as the blog system for consistency.
 */

// Import markdown files directly
import whatAreAiWatermarksEn from '../../knowledge/what-are-ai-watermarks/en.md?raw';
import whatAreAiWatermarksDe from '../../knowledge/what-are-ai-watermarks/de.md?raw';
import whatAreAiWatermarksBr from '../../knowledge/what-are-ai-watermarks/br.md?raw';
import whatAreAiWatermarksMy from '../../knowledge/what-are-ai-watermarks/my.md?raw';
import whatAreAiWatermarksEs from '../../knowledge/what-are-ai-watermarks/es.md?raw';
import whatAreAiWatermarksFr from '../../knowledge/what-are-ai-watermarks/fr.md?raw';
import whatAreAiWatermarksJa from '../../knowledge/what-are-ai-watermarks/ja.md?raw';
import whatAreAiWatermarksId from '../../knowledge/what-are-ai-watermarks/id.md?raw';

import watermarkRemovalVsAiDetectionEn from '../../knowledge/watermark-removal-vs-ai-detection/en.md?raw';
import watermarkRemovalVsAiDetectionDe from '../../knowledge/watermark-removal-vs-ai-detection/de.md?raw';
import watermarkRemovalVsAiDetectionBr from '../../knowledge/watermark-removal-vs-ai-detection/br.md?raw';
import watermarkRemovalVsAiDetectionMy from '../../knowledge/watermark-removal-vs-ai-detection/my.md?raw';
import watermarkRemovalVsAiDetectionEs from '../../knowledge/watermark-removal-vs-ai-detection/es.md?raw';
import watermarkRemovalVsAiDetectionFr from '../../knowledge/watermark-removal-vs-ai-detection/fr.md?raw';
import watermarkRemovalVsAiDetectionJa from '../../knowledge/watermark-removal-vs-ai-detection/ja.md?raw';
import watermarkRemovalVsAiDetectionId from '../../knowledge/watermark-removal-vs-ai-detection/id.md?raw';

import tokenDistributionAiWatermarkingEn from '../../knowledge/token-distribution-ai-watermarking/en.md?raw';
import tokenDistributionAiWatermarkingDe from '../../knowledge/token-distribution-ai-watermarking/de.md?raw';
import tokenDistributionAiWatermarkingBr from '../../knowledge/token-distribution-ai-watermarking/br.md?raw';
import tokenDistributionAiWatermarkingMy from '../../knowledge/token-distribution-ai-watermarking/my.md?raw';
import tokenDistributionAiWatermarkingEs from '../../knowledge/token-distribution-ai-watermarking/es.md?raw';
import tokenDistributionAiWatermarkingFr from '../../knowledge/token-distribution-ai-watermarking/fr.md?raw';
import tokenDistributionAiWatermarkingJa from '../../knowledge/token-distribution-ai-watermarking/ja.md?raw';
import tokenDistributionAiWatermarkingId from '../../knowledge/token-distribution-ai-watermarking/id.md?raw';

import whyAiDetectorsFailEn from '../../knowledge/why-ai-detectors-fail/en.md?raw';
import whyAiDetectorsFailDe from '../../knowledge/why-ai-detectors-fail/de.md?raw';
import whyAiDetectorsFailBr from '../../knowledge/why-ai-detectors-fail/br.md?raw';
import whyAiDetectorsFailMy from '../../knowledge/why-ai-detectors-fail/my.md?raw';
import whyAiDetectorsFailEs from '../../knowledge/why-ai-detectors-fail/es.md?raw';
import whyAiDetectorsFailFr from '../../knowledge/why-ai-detectors-fail/fr.md?raw';
import whyAiDetectorsFailJa from '../../knowledge/why-ai-detectors-fail/ja.md?raw';
import whyAiDetectorsFailId from '../../knowledge/why-ai-detectors-fail/id.md?raw';

import whyShortTextsBreakDetectionEn from '../../knowledge/why-short-texts-break-detection/en.md?raw';
import whyShortTextsBreakDetectionDe from '../../knowledge/why-short-texts-break-detection/de.md?raw';
import whyShortTextsBreakDetectionBr from '../../knowledge/why-short-texts-break-detection/br.md?raw';
import whyShortTextsBreakDetectionMy from '../../knowledge/why-short-texts-break-detection/my.md?raw';
import whyShortTextsBreakDetectionEs from '../../knowledge/why-short-texts-break-detection/es.md?raw';
import whyShortTextsBreakDetectionFr from '../../knowledge/why-short-texts-break-detection/fr.md?raw';
import whyShortTextsBreakDetectionJa from '../../knowledge/why-short-texts-break-detection/ja.md?raw';
import whyShortTextsBreakDetectionId from '../../knowledge/why-short-texts-break-detection/id.md?raw';

export interface KnowledgeArticle {
  slug: string;
  title: string;
  content: string;
  metaTitle: string;
  metaDescription: string;
}

// Define all knowledge articles by slug and locale
const knowledgeArticlesData: Record<string, Record<string, string>> = {
  'what-are-ai-watermarks': {
    en: whatAreAiWatermarksEn,
    de: whatAreAiWatermarksDe,
    'pt-BR': whatAreAiWatermarksBr,
    'ms-MY': whatAreAiWatermarksMy,
    es: whatAreAiWatermarksEs,
    fr: whatAreAiWatermarksFr,
    ja: whatAreAiWatermarksJa,
    id: whatAreAiWatermarksId,
  },
  'watermark-removal-vs-ai-detection': {
    en: watermarkRemovalVsAiDetectionEn,
    de: watermarkRemovalVsAiDetectionDe,
    'pt-BR': watermarkRemovalVsAiDetectionBr,
    'ms-MY': watermarkRemovalVsAiDetectionMy,
    es: watermarkRemovalVsAiDetectionEs,
    fr: watermarkRemovalVsAiDetectionFr,
    ja: watermarkRemovalVsAiDetectionJa,
    id: watermarkRemovalVsAiDetectionId,
  },
  'token-distribution-ai-watermarking': {
    en: tokenDistributionAiWatermarkingEn,
    de: tokenDistributionAiWatermarkingDe,
    'pt-BR': tokenDistributionAiWatermarkingBr,
    'ms-MY': tokenDistributionAiWatermarkingMy,
    es: tokenDistributionAiWatermarkingEs,
    fr: tokenDistributionAiWatermarkingFr,
    ja: tokenDistributionAiWatermarkingJa,
    id: tokenDistributionAiWatermarkingId,
  },
  'why-ai-detectors-fail': {
    en: whyAiDetectorsFailEn,
    de: whyAiDetectorsFailDe,
    'pt-BR': whyAiDetectorsFailBr,
    'ms-MY': whyAiDetectorsFailMy,
    es: whyAiDetectorsFailEs,
    fr: whyAiDetectorsFailFr,
    ja: whyAiDetectorsFailJa,
    id: whyAiDetectorsFailId,
  },
  'why-short-texts-break-detection': {
    en: whyShortTextsBreakDetectionEn,
    de: whyShortTextsBreakDetectionDe,
    'pt-BR': whyShortTextsBreakDetectionBr,
    'ms-MY': whyShortTextsBreakDetectionMy,
    es: whyShortTextsBreakDetectionEs,
    fr: whyShortTextsBreakDetectionFr,
    ja: whyShortTextsBreakDetectionJa,
    id: whyShortTextsBreakDetectionId,
  },
};

/**
 * Extract the first sentences from markdown content for use as description
 *
 * @param content - The markdown content
 * @param maxLength - Maximum length of the description (default: 300 for card descriptions)
 * @returns The extracted description
 */
function extractDescription(content: string, maxLength: number = 300): string {
  // Remove the first H1 or H2 title
  let text = content.replace(/^#{1,2}\s+.+$/m, '').trim();

  // Remove markdown formatting (headers, bold, italic, links, code)
  text = text.replace(/^#+\s+/gm, ''); // Remove headers
  text = text.replace(/\*\*(.+?)\*\*/g, '$1'); // Remove bold
  text = text.replace(/\*(.+?)\*/g, '$1'); // Remove italic
  text = text.replace(/\[(.+?)\]\(.+?\)/g, '$1'); // Remove links, keep text
  text = text.replace(/`(.+?)`/g, '$1'); // Remove inline code
  text = text.replace(/^[-*]\s+/gm, ''); // Remove list markers

  // Get first paragraph or sentences
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0);
  let description = paragraphs[0] || '';

  // Add more paragraphs to get 2-3 sentences
  let currentLength = description.length;
  for (let i = 1; i < paragraphs.length && currentLength < 200; i++) {
    description += ' ' + paragraphs[i];
    currentLength = description.length;
  }

  // Trim to maxLength at sentence boundary
  if (description.length > maxLength) {
    const sentences = description.match(/[^.!?]+[.!?]+/g) || [];
    description = '';
    for (const sentence of sentences) {
      if ((description + sentence).length <= maxLength) {
        description += sentence;
      } else {
        break;
      }
    }

    // If no complete sentence fits, truncate and add ellipsis
    if (!description && sentences.length > 0) {
      description = sentences[0].substring(0, maxLength - 3) + '...';
    }
  }

  return description.trim();
}

/**
 * Load a knowledge article by its English slug and locale
 *
 * @param slug - The English canonical slug
 * @param locale - The locale to load (en, de, pt-BR, ms-MY, es)
 * @returns The knowledge article or undefined if not found
 */
export function getArticleBySlug(slug: string, locale: string): KnowledgeArticle | undefined {
  try {
    const articleData = knowledgeArticlesData[slug];
    if (!articleData) {
      console.warn(`[Knowledge] Article not found: ${slug}`);
      return undefined;
    }

    const content = articleData[locale];
    if (!content) {
      console.warn(`[Knowledge] Article locale not found: ${slug}/${locale}`);
      return undefined;
    }

    // Extract title from first H1 or H2
    const titleMatch = content.match(/^#{1,2}\s+(.+)$/m);
    const title = titleMatch ? titleMatch[1] : slug;

    // Extract description from first sentences of the article
    const metaDescription = extractDescription(content);
    const metaTitle = title;

    return {
      slug,
      title,
      content,
      metaTitle,
      metaDescription,
    };
  } catch (error) {
    console.error(`[Knowledge] Error loading article ${slug}:`, error);
    return undefined;
  }
}

/**
 * Get all knowledge articles for a given locale
 *
 * @param locale - The locale to load
 * @returns Array of all knowledge articles
 */
export function getAllArticles(locale: string): KnowledgeArticle[] {
  try {
    // Get all article slugs from the data structure
    const slugs = Object.keys(knowledgeArticlesData);

    // Load each article
    const articles = slugs
      .map((slug) => getArticleBySlug(slug, locale))
      .filter((article): article is KnowledgeArticle => article !== undefined);

    return articles;
  } catch (error) {
    console.error('[Knowledge] Error loading all articles:', error);
    return [];
  }
}
